
import { AlertCircle, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TaskConfig } from '../types';
import type { AslState } from '@/types/workflow-graph';

interface AdvancedTabProps {
    config: TaskConfig;
    aslPreview: Partial<AslState>;
    onCommentChange: (comment: string) => void;
    validationErrors: Record<string, string>;
    readOnly: boolean;
}

export function AdvancedTab({
    config,
    aslPreview,
    onCommentChange,
    validationErrors,
    readOnly,
}: AdvancedTabProps) {
    const [copied, setCopied] = useState(false);
    const errorCount = Object.keys(validationErrors).length;
    const aslJson = JSON.stringify(aslPreview, null, 2);

    const handleCopyAsl = async () => {
        try {
            await navigator.clipboard.writeText(aslJson);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div className="p-6 space-y-8">
            {/* Comment/Description */}
            <section className="space-y-3">
                <Label className="text-base font-semibold">Comment / Description</Label>
                <Textarea
                    value={config.comment || ''}
                    onChange={(e) => onCommentChange(e.target.value)}
                    placeholder="Add a description for this task..."
                    rows={3}
                    disabled={readOnly}
                    className="resize-none"
                />
                <p className="text-xs text-slate-500">
                    This comment will be included in the exported ASL definition.
                </p>
            </section>

            {/* Validation Summary */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Validation</Label>
                    {errorCount > 0 ? (
                        <Badge variant="destructive" className="text-[10px]">
                            {errorCount} error{errorCount !== 1 ? 's' : ''}
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                            Valid
                        </Badge>
                    )}
                </div>

                {errorCount > 0 ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
                        {Object.entries(validationErrors).map(([key, message]) => (
                            <div key={key} className="flex items-start gap-2 text-sm">
                                <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                                <div>
                                    <span className="font-mono text-xs text-red-700">{key}:</span>
                                    <span className="text-red-600 ml-1">{message}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                        Configuration is valid and ready to save.
                    </div>
                )}
            </section>

            {/* Configuration Summary */}
            <section className="space-y-3">
                <Label className="text-base font-semibold">Configuration Summary</Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-slate-500">Capability</span>
                        <span className="font-mono text-slate-900">
                            {config.capabilityKey || '(not selected)'}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Input Path</span>
                        <span className="font-mono text-slate-900">{config.inputPath}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Result Placement</span>
                        <span className="font-mono text-slate-900">{config.resultPlacement}</span>
                    </div>
                    {config.resultPlacement === 'merge' && (
                        <div className="flex justify-between">
                            <span className="text-slate-500">Result Path</span>
                            <span className="font-mono text-slate-900">{config.resultPath}</span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span className="text-slate-500">Parameters</span>
                        <span className="font-mono text-slate-900">
                            {Object.keys(config.parameters).length} mapped
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Retry Rules</span>
                        <span className="font-mono text-slate-900">
                            {config.retry.length} rule{config.retry.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-500">Catch Rules</span>
                        <span className="font-mono text-slate-900">
                            {config.catch.length} rule{config.catch.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    {config.timeoutSeconds && (
                        <div className="flex justify-between">
                            <span className="text-slate-500">Timeout</span>
                            <span className="font-mono text-slate-900">{config.timeoutSeconds}s</span>
                        </div>
                    )}
                </div>
            </section>

            {/* ASL Preview */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">ASL Preview</Label>
                    <button
                        onClick={handleCopyAsl}
                        className={cn(
                            "flex items-center gap-1.5 text-xs font-medium transition-colors",
                            copied
                                ? "text-green-600"
                                : "text-blue-600 hover:text-blue-700"
                        )}
                    >
                        {copied ? (
                            <>
                                <Check size={12} />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy size={12} />
                                Copy JSON
                            </>
                        )}
                    </button>
                </div>
                <pre className="rounded-lg bg-slate-900 p-4 text-xs text-slate-100 font-mono overflow-auto max-h-[300px]">
                    <code>{aslJson}</code>
                </pre>
                <p className="text-xs text-slate-500">
                    Read-only preview of the compiled ASL Task state definition.
                </p>
            </section>

            {/* Custom Attributes (Placeholder) */}
            <section className="space-y-3">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold text-slate-400">
                        Custom Span Attributes
                    </Label>
                    <Badge variant="outline" className="text-[10px]">
                        Coming Soon
                    </Badge>
                </div>
                <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                    Add custom observability attributes in a future update.
                </div>
            </section>
        </div>
    );
}

